import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import {
  adminApi,
  type AgentRole,
  type TeamTemplate,
  type TeamTemplateSortField,
  type WorkflowStepInput,
} from '../api/admin';
import { useAgentRoles, type RoleOption, type TypeOption } from '../lib/use-agent-roles';
import { DataListFrame, SortHeader, stickyTh, useListControls } from '../components/data-list';
import {
  WorkflowStepsEditor,
  draftFromStep,
  draftsToInput,
  type WorkflowDraft,
} from '../components/workflow-editor';

const SORT_OPTIONS: { value: TeamTemplateSortField; label: string }[] = [
  { value: 'id', label: 'ID' },
  { value: 'name', label: 'テンプレ名' },
  { value: 'createdAt', label: '作成日' },
];

// チームテンプレート = 役割構成の雛形。チーム作成時にこの構成をコピーして役割スロットを生成する。
// テンプレを後で直しても、作成済みチームには影響しない（コピー方式・マスター非追従）。
// 一覧はサーバ側 検索/ソート/ページング（条件は localStorage 永続化）。
export function TeamTemplatesPage() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['team-templates'] });

  const controls = useListControls<TeamTemplateSortField>('konecco.list.team-templates', {
    q: '',
    sortBy: 'id',
    sortDir: 'asc',
    page: 1,
    pageSize: 20,
  });
  // 区分(type) = ドロップダウン絞り込み（マスタ）。'' は指定なし。
  const [typeFilter, setTypeFilter] = useState('');
  const query = useQuery({
    queryKey: [
      'team-templates',
      'search',
      controls.q,
      typeFilter,
      controls.sortBy,
      controls.sortDir,
      controls.page,
      controls.pageSize,
    ],
    queryFn: () =>
      adminApi.searchTeamTemplates({
        q: controls.q,
        type: typeFilter || undefined,
        sortBy: controls.sortBy,
        sortDir: controls.sortDir,
        page: controls.page,
        pageSize: controls.pageSize,
      }),
    placeholderData: keepPreviousData,
  });
  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  // 区分・役割の選択肢／ラベルはマスタ管理(#176)から供給する。役割は区分でカスケード。
  const { typeOptions, typeLabelOf, roleOptionsByType, roleLabelOf } = useAgentRoles();

  const [createOpen, setCreateOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<TeamTemplate | null>(null);

  const createTemplate = useMutation({
    mutationFn: adminApi.createTeamTemplate,
    onSuccess: () => {
      setCreateOpen(false);
      invalidate();
    },
  });
  const updateTemplate = useMutation({
    mutationFn: (vars: { id: number; input: TemplateFormValues }) =>
      adminApi.updateTeamTemplate(vars.id, vars.input),
    onSuccess: () => {
      setEditTarget(null);
      invalidate();
    },
  });
  const deleteTemplate = useMutation({
    mutationFn: adminApi.deleteTeamTemplate,
    onSuccess: invalidate,
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <p className="k-muted" style={{ margin: 0, fontSize: 13 }}>
        役割構成の雛形です。チーム作成時にテンプレを選ぶと、この構成（役割の枠）がコピーされます。あとでテンプレを直しても既存チームは変わりません。
      </p>
      {query.error && (
        <p className="k-error">取得に失敗しました: {(query.error as Error).message}</p>
      )}
      {deleteTemplate.error && (
        <p className="k-error">削除に失敗しました: {(deleteTemplate.error as Error).message}</p>
      )}
      <DataListFrame
        title="チームテンプレート"
        total={total}
        loading={query.isLoading}
        controls={controls}
        sortOptions={SORT_OPTIONS}
        searchPlaceholder="あいまい検索…（テンプレ名・説明）"
        filters={
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span className="k-muted" style={{ fontSize: 12 }}>
              区分
            </span>
            <select
              value={typeFilter}
              onChange={(e) => {
                setTypeFilter(e.target.value);
                controls.setPage(1);
              }}
              style={{ padding: '7px 10px', fontSize: 13 }}
            >
              <option value="">すべて</option>
              {typeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        }
        rightActions={
          <button type="button" onClick={() => setCreateOpen(true)}>
            ＋ 新規作成
          </button>
        }
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortHeader field="id" controls={controls}>id</SortHeader>
              <SortHeader field="name" controls={controls}>テンプレ名</SortHeader>
              <th style={stickyTh}>説明</th>
              <th style={stickyTh}>構成（役割の並び）</th>
              <th style={stickyTh}>操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                <td style={td}>{t.id}</td>
                <td style={td}>
                  <span style={{ marginRight: 6 }}>{t.icon || '📋'}</span>
                  <strong>{t.name}</strong>
                  <span
                    className="k-badge"
                    title="区分"
                    style={{ marginLeft: 8, background: '#eef2ff', color: '#3730a3' }}
                  >
                    {typeLabelOf(t.type)}
                  </span>
                </td>
                <td style={{ ...td, maxWidth: 220, color: '#475569' }}>
                  {t.description ? (
                    <span title={t.description}>
                      {t.description.length > 36 ? `${t.description.slice(0, 36)}…` : t.description}
                    </span>
                  ) : (
                    <span className="k-muted">—</span>
                  )}
                </td>
                <td style={td}>
                  {t.slots && t.slots.length > 0 ? (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      {t.slots.map((s, i) => (
                        <span
                          key={s.id}
                          className="k-badge"
                          title={s.isLeader ? 'リーダー(PM)枠' : undefined}
                          style={s.isLeader ? { background: '#fffbeb', color: '#92400e' } : undefined}
                        >
                          {s.isLeader ? '👑 ' : `${i + 1}. `}
                          {roleLabelOf(s.role)}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="k-muted">役割なし</span>
                  )}
                  {t.workflowSteps && t.workflowSteps.length > 0 && (
                    <div style={{ marginTop: 4 }}>
                      <span
                        className="k-badge"
                        title="ワークフロー工程数"
                        style={{ background: '#ecfdf5', color: '#047857' }}
                      >
                        ⚙️ ワークフロー {t.workflowSteps.length}工程
                      </span>
                    </div>
                  )}
                </td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <button className="btn-ghost" type="button" onClick={() => setEditTarget(t)}>
                      変更
                    </button>
                    <button
                      className="btn-danger"
                      type="button"
                      disabled={deleteTemplate.isPending}
                      onClick={() => {
                        if (
                          confirm(
                            `テンプレート「${t.name}」を削除しますか？\n（作成済みチームの構成はコピー済みなので残ります）`,
                          )
                        ) {
                          deleteTemplate.mutate(t.id);
                        }
                      }}
                    >
                      削除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </DataListFrame>

      {createOpen && (
        <TemplateFormDialog
          mode="create"
          typeOptions={typeOptions}
          roleOptionsByType={roleOptionsByType}
          roleLabelOf={roleLabelOf}
          submitting={createTemplate.isPending}
          error={createTemplate.error as Error | null}
          onClose={() => setCreateOpen(false)}
          onSubmit={(v) => createTemplate.mutate(v)}
        />
      )}
      {editTarget && (
        <TemplateFormDialog
          mode="edit"
          initial={editTarget}
          typeOptions={typeOptions}
          roleOptionsByType={roleOptionsByType}
          roleLabelOf={roleLabelOf}
          submitting={updateTemplate.isPending}
          error={updateTemplate.error as Error | null}
          onClose={() => setEditTarget(null)}
          onSubmit={(v) => updateTemplate.mutate({ id: editTarget.id, input: v })}
        />
      )}
    </div>
  );
}

interface TemplateFormValues {
  name: string;
  type: string;
  icon: string | null;
  description: string | null;
  roles: AgentRole[];
  leaderIndex: number; // roles 上のリーダー(PM)枠の位置
  workflow: WorkflowStepInput[];
}

function TemplateFormDialog({
  mode,
  initial,
  typeOptions,
  roleOptionsByType,
  roleLabelOf,
  onSubmit,
  onClose,
  submitting,
  error,
}: {
  mode: 'create' | 'edit';
  initial?: TeamTemplate;
  typeOptions: TypeOption[];
  roleOptionsByType: (typeCode: string) => RoleOption[];
  roleLabelOf: (role: AgentRole) => string;
  onSubmit: (v: TemplateFormValues) => void;
  onClose: () => void;
  submitting: boolean;
  error: Error | null;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [type, setType] = useState<string>(initial?.type ?? '');
  const [icon, setIcon] = useState(initial?.icon ?? '');
  const [description, setDescription] = useState(initial?.description ?? '');
  const [roles, setRoles] = useState<AgentRole[]>(
    () => initial?.slots?.map((s) => s.role) ?? [],
  );
  // リーダー(PM)枠の位置（roles の index）。既存テンプレは isLeader の枠を優先。
  // 無ければ既定は「PM兼設計者(pm_se)」枠、それも無ければ先頭。
  const [leaderIndex, setLeaderIndex] = useState<number>(() => {
    const flagged = initial?.slots?.findIndex((s) => s.isLeader) ?? -1;
    if (flagged >= 0) return flagged;
    const pm = initial?.slots?.findIndex((s) => s.role === 'pm_se') ?? -1;
    return pm >= 0 ? pm : 0;
  });
  // ワークフロー工程（工程フロー図）。テンプレに保持し、チーム作成時にコピーされる。
  const [workflow, setWorkflow] = useState<WorkflowDraft[]>(
    () => (initial?.workflowSteps ?? []).map(draftFromStep),
  );
  // 区分はマスタ先頭を既定に（マスタ非同期取得のため空時はフォールバック）。役割はこの区分でカスケード。
  const effectiveType = type || typeOptions[0]?.value || '';
  const roleOptions = effectiveType ? roleOptionsByType(effectiveType) : [];
  // 区分を変えたら、新区分に属さない役割は枠から外す（直感的に「区分を変えると合わない役割は消える」）。
  // ワークフロー工程の担当役割も同様に、新区分に属さないものは「担当なし」に戻す。
  const onChangeType = (next: string) => {
    setType(next);
    const valid = new Set(roleOptionsByType(next).map((o) => o.value));
    setRoles((prev) => prev.filter((r) => valid.has(r)));
    setWorkflow((prev) =>
      prev.map((s) =>
        s.ownerRole !== 'unset' && !valid.has(s.ownerRole) ? { ...s, ownerRole: 'unset' } : s,
      ),
    );
  };
  // 役割追加時の既定はその区分の先頭役割。区分未選択/役割なしなら追加不可。
  const addRole = () => {
    const first = roleOptions[0]?.value;
    if (first) setRoles((prev) => [...prev, first]);
  };
  const setRoleAt = (i: number, r: AgentRole) =>
    setRoles((prev) => prev.map((x, idx) => (idx === i ? r : x)));
  // 削除時はリーダー位置も追従（消えた枠がリーダーなら先頭に戻す／前を消したら1つ詰める）。
  const removeRole = (i: number) => {
    setRoles((prev) => prev.filter((_, idx) => idx !== i));
    setLeaderIndex((cur) => (cur === i ? 0 : cur > i ? cur - 1 : cur));
  };
  // 並べ替え時はリーダー位置も一緒に入れ替える（同じ役割枠を指し続ける）。
  const moveRole = (i: number, dir: -1 | 1) => {
    const j = i + dir;
    if (j < 0 || j >= roles.length) return;
    setRoles((prev) => {
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
    setLeaderIndex((cur) => (cur === i ? j : cur === j ? i : cur));
  };
  const labelStyle = { fontSize: 13, fontWeight: 600, color: '#334155' } as const;
  const fieldWrap = { display: 'flex', flexDirection: 'column', gap: 4 } as const;

  return (
    <Modal
      title={mode === 'create' ? '新しいチームテンプレートを作成' : `テンプレートを変更 — #${initial?.id}`}
      onClose={onClose}
      width={620}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (!name.trim() || !effectiveType) return;
          onSubmit({
            name: name.trim(),
            type: effectiveType,
            icon: icon.trim() || null,
            description: description.trim() || null,
            roles,
            leaderIndex: roles.length > 0 ? Math.min(leaderIndex, roles.length - 1) : 0,
            workflow: draftsToInput(workflow),
          });
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
      >
        <div style={{ display: 'flex', gap: 10 }}>
          <div style={{ ...fieldWrap, width: 88 }}>
            <label style={labelStyle}>アイコン</label>
            <input
              placeholder="📋"
              value={icon}
              onChange={(e) => setIcon(e.target.value)}
              style={{ textAlign: 'center', fontSize: 20 }}
              maxLength={4}
            />
          </div>
          <div style={{ ...fieldWrap, flex: 1 }}>
            <label style={labelStyle}>テンプレ名（例: 本番運用チーム）</label>
            <input
              placeholder="例: 本番運用チーム"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
          </div>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>区分（開発系／会社運用系…）</label>
          <select
            value={effectiveType}
            disabled={typeOptions.length === 0}
            onChange={(e) => onChangeType(e.target.value)}
          >
            {typeOptions.length === 0 ? (
              <option value="">区分マスタがありません</option>
            ) : (
              typeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))
            )}
          </select>
          <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
            このテンプレの役割は、選んだ区分の責務だけから選べます（区分を変えると合わない役割は外れます）。
          </p>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>説明（任意）</label>
          <textarea
            placeholder="例: PM・実装・QA・デプロイ担当をそろえた本番運用の標準構成。"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>役割構成（上から順に枠が作られます）— {roles.length}枠</label>
          <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 0' }}>
            👑＝リーダー(PM)枠。このテンプレから作ったチームでは、チャットの宛先(@)未指定時にこの枠の人が応答します。
          </p>
          {roles.length === 0 ? (
            <p className="k-muted" style={{ fontSize: 13, margin: '2px 0' }}>
              まだ役割がありません。「＋ 役割を追加」で枠を足してください。
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {roles.map((r, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="k-muted" style={{ width: 18, textAlign: 'right', fontSize: 13 }}>
                    {i + 1}.
                  </span>
                  {/* 👑 リーダー(PM)枠の単一選択。押した枠がリーダーになる。 */}
                  <button
                    type="button"
                    onClick={() => setLeaderIndex(i)}
                    disabled={leaderIndex === i}
                    aria-label={leaderIndex === i ? 'リーダー(PM)枠' : 'リーダー(PM)枠に設定'}
                    title={leaderIndex === i ? 'リーダー(PM)枠' : 'クリックでリーダー(PM)枠に設定'}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      cursor: leaderIndex === i ? 'default' : 'pointer',
                      fontSize: 16,
                      lineHeight: 1,
                      padding: 2,
                      opacity: leaderIndex === i ? 1 : 0.3,
                    }}
                  >
                    👑
                  </button>
                  <select
                    value={r}
                    onChange={(e) => setRoleAt(i, e.target.value as AgentRole)}
                    style={{ flex: 1 }}
                  >
                    {/* 既存値がマスタに無い（無効化済み等）場合も値が消えないよう先頭に補う */}
                    {!roleOptions.some((o) => o.value === r) && (
                      <option value={r}>{roleLabelOf(r)}</option>
                    )}
                    {roleOptions.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  <button
                    className="btn-ghost"
                    type="button"
                    onClick={() => moveRole(i, -1)}
                    disabled={i === 0}
                    aria-label="上へ"
                    style={{ padding: '6px 9px' }}
                  >
                    ↑
                  </button>
                  <button
                    className="btn-ghost"
                    type="button"
                    onClick={() => moveRole(i, 1)}
                    disabled={i === roles.length - 1}
                    aria-label="下へ"
                    style={{ padding: '6px 9px' }}
                  >
                    ↓
                  </button>
                  <button
                    className="btn-danger"
                    type="button"
                    onClick={() => removeRole(i)}
                    aria-label="削除"
                    style={{ padding: '6px 9px' }}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}
          <button
            className="btn-ghost"
            type="button"
            onClick={addRole}
            disabled={roleOptions.length === 0}
            style={{ alignSelf: 'flex-start', marginTop: 4 }}
          >
            ＋ 役割を追加
          </button>
        </div>
        <div style={fieldWrap}>
          <label style={labelStyle}>
            ワークフロー（工程フロー図）— {workflow.length}工程
          </label>
          <p className="k-muted" style={{ fontSize: 12, margin: '2px 0 6px' }}>
            このテンプレから作ったチームに、メンバーが「どういうときにどう動くか」の工程が初期投入されます（あとでチーム側で変更可）。担当役割はこの区分の役割から選べます。
          </p>
          <WorkflowStepsEditor
            steps={workflow}
            onChange={setWorkflow}
            roleOptions={roleOptions}
            roleLabelOf={roleLabelOf}
          />
        </div>
        {error && <p className="k-error">{error.message}</p>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button className="btn-ghost" type="button" onClick={onClose}>
            キャンセル
          </button>
          <button type="submit" disabled={submitting || !name.trim() || !effectiveType}>
            {mode === 'create' ? '作成' : '変更を保存'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function Modal({
  title,
  onClose,
  children,
  width = 520,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,23,42,0.45)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '7vh 16px',
        zIndex: 50,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="k-card"
        style={{ width: '100%', maxWidth: width, maxHeight: '85vh', overflow: 'auto' }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 14,
          }}
        >
          <h3 style={{ margin: 0 }}>{title}</h3>
          <button className="btn-ghost" type="button" onClick={onClose} aria-label="閉じる">
            ✕
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

const td = { padding: '8px 12px', fontSize: 14, verticalAlign: 'top' } as const;
